/**
 * Lifecycle Modals — unified design language across all 5 heroes.
 *
 * Three modal components used by `<FlowLifecycleManager>`:
 *
 *   • ResumeModal  — "you have an unfinished {hero} — pick what to do"
 *   • ConfirmModal — generic single-confirm (Restart / Discard / Leave)
 *   • RetakeModal  — confirm + reason picker for assessment retakes
 *
 * Design language (2026-04-27 redesign):
 *
 *   ┌────────────────────────────────┐
 *   │  ✕                             │  always-present escape (X)
 *   │                                │
 *   │   ●●○                          │  intent badge (small, tinted)
 *   │   Restart your CheckUp?        │  headline (≤ 6 words)
 *   │   Step 3 of 6 · 50% complete   │  contextual subline
 *   │   ▓▓▓▓▓▓░░░░░░ 50%             │  optional progress snapshot
 *   │   ──────────────────────────── │
 *   │   ✓ Keeping            ✗ Losing│  two-column Keep/Lose diff
 *   │     • Your last result   • 12 answers
 *   │     • Your CheckUp Miles • Step 3 progress
 *   │   ──────────────────────────── │
 *   │   [⚠ Can't be undone]          │  reversibility chip (when set)
 *   │                                │
 *   │       [ Yes, restart ]         │  primary, intent-colored
 *   │            Stay                │  tertiary text button
 *   └────────────────────────────────┘
 *
 * Principles:
 *   1. Show stakes, not generic warnings — "You'll lose 12 answers" beats
 *      "Are you sure?".
 *   2. One primary action, never two equal-weight buttons. Stay = ghost.
 *   3. Reversibility cue front and center via a dedicated chip.
 *   4. Same frame for all 5 heroes; intent (info / warning / destructive
 *      / success) shifts the hue family. No more per-hero modal feel.
 *   5. Motion as feedback: scale-in entrance, button arming sweep.
 *
 * Destructive modals (Restart / Discard / Retake) honour the registry's
 * `arming` flag — the confirm button is disabled for ARMING_DELAY_MS
 * after the modal opens AND its border traces a 1-second sweep so the
 * user sees the protection visually, not just feels the mis-click.
 */

import { useTranslation } from '@tonnie/locale';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from '@tonnie/ui';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  Loader2,
  Lock,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
} from '@tonnie/assets';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import type { ConfirmModalConfig, KeepLoseItem, LifecycleIntent, PreFlightItem } from './types';

// ────────────────────────────────────────────────────────────────────
// Constants + hooks
// ────────────────────────────────────────────────────────────────────

/**
 * How long to disable the confirm button after the modal opens.
 * Long enough to defeat a fast accidental tap (~600ms human reaction
 * time + buffer); short enough to feel snappy. Industry pattern —
 * Stripe / GitHub use ~1s on irreversible deletes.
 */
const ARMING_DELAY_MS = 1000;

/**
 * Hook that returns `false` for the first ARMING_DELAY_MS after
 * `open` flips true, then `true`. Used to gate destructive confirms.
 */
function useArming(open: boolean, enabled: boolean): boolean {
  const [armed, setArmed] = useState(!enabled);
  useEffect(() => {
    if (!open || !enabled) {
      setArmed(!enabled);
      return;
    }
    setArmed(false);
    const t = setTimeout(() => setArmed(true), ARMING_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, enabled]);
  return armed;
}

// ────────────────────────────────────────────────────────────────────
// Intent → palette mapping
// ────────────────────────────────────────────────────────────────────

interface IntentPalette {
  /** Soft tint for the badge background. */
  badgeBg: string;
  /** Foreground color for the badge icon. */
  badgeFg: string;
  /** Ring around the badge (subtle outline). */
  badgeRing: string;
  /** The confirm button's variant prop on @tonnie/ui Button. */
  buttonVariant: 'default' | 'destructive';
  /** Optional Tailwind override on the confirm button to swap to amber
   *  for `warning` intent (the design system has no `warning` Button
   *  variant — we paint amber on top of `default`). */
  buttonClassName?: string;
}

const INTENT_PALETTES: Record<LifecycleIntent, IntentPalette> = {
  info: {
    badgeBg: 'bg-primary/10 dark:bg-primary/15',
    badgeFg: 'text-primary-accessible',
    badgeRing: 'ring-primary/20',
    buttonVariant: 'default',
  },
  warning: {
    badgeBg: 'bg-tone-amber-subtle',
    badgeFg: 'text-tone-amber',
    badgeRing: 'ring-tone-amber-line',
    buttonVariant: 'default',
    buttonClassName: 'bg-tone-amber-solid hover:bg-tone-amber-solid-hover text-tone-amber-on-solid',
  },
  destructive: {
    badgeBg: 'bg-destructive/10 dark:bg-destructive/15',
    badgeFg: 'text-destructive-accessible',
    badgeRing: 'ring-destructive/20',
    buttonVariant: 'destructive',
  },
  success: {
    badgeBg: 'bg-tone-green-subtle',
    badgeFg: 'text-tone-green',
    badgeRing: 'ring-tone-green-line',
    buttonVariant: 'default',
  },
};

// ────────────────────────────────────────────────────────────────────
// Building blocks
// ────────────────────────────────────────────────────────────────────

/**
 * Small badge at the top of a lifecycle modal — single source of
 * intent-as-color. Replaces the old `ModalHero` (which was 14×14
 * with ring-2 and felt heavy). The redesign uses a 12×12 chip with a
 * 1px ring; intent comes through the tint, not the size.
 */
function IntentBadge({
  intent,
  icon: Icon,
}: {
  intent: LifecycleIntent;
  icon: React.ComponentType<{ className?: string }>;
}) {
  const p = INTENT_PALETTES[intent];
  return (
    <div
      className={cn(
        'mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ring-1',
        p.badgeBg,
        p.badgeRing
      )}
      aria-hidden="true"
    >
      <Icon className={cn('h-6 w-6', p.badgeFg)} />
    </div>
  );
}

/**
 * Two-column "Keep ✓ / Lose ✗" visualizer. The single most useful
 * piece of information in any destructive modal: a glance-readable
 * diff of what survives vs what dies. Replaces walls of body copy.
 *
 * Renders nothing when both columns are empty — the modal falls back
 * to its body text. When only one column has items the other side
 * stays empty (and we render a neutral placeholder caption to keep
 * the visual symmetry).
 */
function KeepLoseList({
  keep,
  lose,
  interpolation,
}: {
  keep?: KeepLoseItem[];
  lose?: KeepLoseItem[];
  interpolation: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const keepItems = keep ?? [];
  const loseItems = lose ?? [];
  if (keepItems.length === 0 && loseItems.length === 0) return null;

  // When one side is intentionally empty (e.g. Journey Builder restart
  // has nothing to keep), rendering both columns with an "italic
  // placeholder" caption looks unbalanced — the empty column is the
  // visual equivalent of a stage whisper. Drop to a single full-width
  // column when only one side has content. Radar 2026-04-28 — UX D-3.
  const onlyKeep = keepItems.length > 0 && loseItems.length === 0;
  const onlyLose = loseItems.length > 0 && keepItems.length === 0;
  const layoutClass =
    onlyKeep || onlyLose ? 'grid grid-cols-1 gap-3 py-1' : 'grid grid-cols-2 gap-3 py-1';

  return (
    <div className={layoutClass} role="group" aria-label={t('copilot.lifecycle.aria.whatChanges')}>
      {!onlyLose && (
        <div className="rounded-lg border border-tone-green-line bg-tone-green-subtle px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Check className="h-3.5 w-3.5 text-tone-green" aria-hidden />
            <span className="text-2xs font-semibold tracking-wide uppercase text-tone-green">
              {t('copilot.lifecycle.keep.title')}
            </span>
          </div>
          {keepItems.length > 0 ? (
            <ul className="space-y-1 text-xs leading-snug text-foreground/85">
              {keepItems.map((item) => (
                <li key={item.labelKey} className="flex items-start gap-1.5">
                  <span className="text-tone-green mt-[3px] shrink-0" aria-hidden>
                    •
                  </span>
                  <span>{t(item.labelKey, { ...interpolation })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-2xs text-muted-foreground italic">
              {t('copilot.lifecycle.keep.empty')}
            </p>
          )}
        </div>
      )}

      {!onlyKeep && (
        <div className="rounded-lg border border-tone-rose-line bg-tone-rose-subtle px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Trash2 className="h-3.5 w-3.5 text-tone-rose" aria-hidden />
            <span className="text-2xs font-semibold tracking-wide uppercase text-tone-rose">
              {t('copilot.lifecycle.lose.title')}
            </span>
          </div>
          {loseItems.length > 0 ? (
            <ul className="space-y-1 text-xs leading-snug text-foreground/85">
              {loseItems.map((item) => (
                <li key={item.labelKey} className="flex items-start gap-1.5">
                  <span className="text-tone-rose mt-[3px] shrink-0" aria-hidden>
                    •
                  </span>
                  <span>{t(item.labelKey, { ...interpolation })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-2xs text-muted-foreground italic">
              {t('copilot.lifecycle.lose.empty')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Reversibility chip — a single inline pill that says "Reversible" or
 * "Can't be undone" with a matching icon. Always present when the
 * registry sets `reversible: true | false`; hidden when it's left
 * `undefined` (the answer is genuinely ambiguous and we'd mislead).
 */
function ReversibilityChip({ reversible }: { reversible: boolean | undefined }) {
  const { t } = useTranslation();
  if (reversible === undefined) return null;

  if (reversible) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full bg-primary/8 dark:bg-primary/12 border border-primary/20 px-2.5 py-1 text-2xs font-medium text-primary-accessible"
        role="note"
      >
        <RotateCcw className="h-3 w-3" aria-hidden />
        {t('copilot.lifecycle.reversible.yes')}
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-destructive/15 dark:bg-destructive/20 border border-destructive/30 px-2.5 py-1 text-2xs font-medium text-destructive-accessible"
      role="note"
    >
      <Lock className="h-3 w-3" aria-hidden />
      {t('copilot.lifecycle.reversible.no')}
    </div>
  );
}

/**
 * Tiny inline progress bar — visible "you're 60% through" feedback
 * stronger than text alone. Inherits hero theming via Tailwind's
 * primary token.
 */
function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="w-full h-1.5 rounded-full bg-muted overflow-hidden"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
    >
      <motion.div
        className="h-full bg-primary"
        initial={{ width: 0 }}
        animate={{ width: `${clamped}%` }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}

/**
 * Confirm button with an arming sweep animation. While `armed` is
 * false (the first ~1s after the modal opens), the button is disabled
 * and a 1px circular-progress sweep traces around its border to make
 * the protection visible — instead of a static disabled state that
 * looks broken.
 *
 * Intent palette controls the color: `destructive` → red, `warning`
 * → amber, `info`/`success` → primary. The button variant follows
 * the palette's `buttonVariant` so contrast tokens stay correct.
 */
function ArmedConfirmButton({
  intent,
  armed,
  busy,
  disabled,
  onClick,
  children,
  trailingIcon,
}: {
  intent: LifecycleIntent;
  armed: boolean;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  trailingIcon?: React.ComponentType<{ className?: string }>;
}) {
  const palette = INTENT_PALETTES[intent];
  const isDisabled = disabled || busy || !armed;

  // The sweep ring sits on a parent wrapper so it can scale around the
  // full button bounds without affecting the button's hit-target.
  return (
    <div className="relative w-full">
      {/* Arming progress sweep — 1px ring traced over 1s. Uses
           `framer-motion` `circle` strokeDasharray trick so the visual
           matches the actual disabled window. Only visible while
           `armed === false` AND the modal is open. */}
      <AnimatePresence>
        {!armed && !busy && (
          <motion.div
            key="arming-sweep"
            className="pointer-events-none absolute inset-0 rounded-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            aria-hidden
          >
            <svg
              className="absolute inset-0 w-full h-full overflow-visible"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              <motion.rect
                x="1"
                y="1"
                width="98"
                height="98"
                rx="6"
                ry="6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                pathLength={1}
                strokeDasharray="1 1"
                strokeDashoffset={1}
                animate={{ strokeDashoffset: 0 }}
                transition={{ duration: ARMING_DELAY_MS / 1000, ease: 'linear' }}
                className={palette.badgeFg}
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        variant={palette.buttonVariant}
        onClick={onClick}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        className={cn('w-full transition-all', palette.buttonClassName)}
      >
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />}
        <span>{children}</span>
        {!busy && trailingIcon && <ArrowRight className="w-4 h-4 ml-2" aria-hidden />}
      </Button>
    </div>
  );
}

/**
 * Tertiary "Stay" / cancel button. Single source of truth for the
 * de-emphasized exit action across all confirm modals. By rendering
 * it as a ghost-style text button below the primary, we break the
 * "two equal-weight buttons" pattern that made every confirm modal
 * feel like a forced binary decision.
 */
function StayButton({
  onClick,
  busy,
  labelKey,
}: {
  onClick: () => void;
  busy?: boolean;
  /** Override the default "Stay" copy — used by Leave modal which
   *  already has its own "Stay and finish this step" key. */
  labelKey?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'mx-auto mt-1 inline-flex items-center justify-center rounded-md',
        'px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground',
        'hover:bg-muted/40 transition-colors',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      {t(labelKey ?? 'copilot.lifecycle.stay')}
    </button>
  );
}

/**
 * Legacy single-list bullet rendering. Kept for back-compat with
 * registry entries that still set `preFlightItems` and haven't moved
 * to the `keep` / `lose` diff. New flows should use the diff.
 */
function PreFlightList({
  items,
  interpolation,
}: {
  items: PreFlightItem[];
  interpolation: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2 py-1" role="list">
      {items.map((item) => {
        const Icon =
          item.tone === 'safe' ? Check : item.tone === 'change' ? AlertCircle : AlertTriangle;
        const color =
          item.tone === 'safe'
            ? 'text-tone-green'
            : item.tone === 'change'
              ? 'text-tone-amber'
              : 'text-destructive-accessible';
        return (
          <li key={item.labelKey} className="flex items-start gap-2 text-sm">
            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} aria-hidden="true" />
            <span className="text-foreground/90">{t(item.labelKey, { ...interpolation })}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Animated entrance wrapper for the modal content. Replaces Radix's
 * default snap-in with a softer scale + fade — closer to the feel of
 * Revolut / Linear / Stripe modals. 200ms is fast enough to not feel
 * laggy, slow enough to register as intentional motion.
 */
function ModalEntrance({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────
// ConfirmModal — Restart / Discard / Leave
// ────────────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  open: boolean;
  config: ConfirmModalConfig;
  /** Translation context (hero, progress, step) for body interpolation. */
  context?: {
    progressPercent?: number;
    currentStep?: number;
    totalSteps?: number;
  };
  busy?: boolean;
  destructive?: boolean;
  /**
   * Visual intent of the icon hero area. Falls back to a sensible
   * default per-modal-type when omitted (Leave→info, Restart→warning,
   * Discard→destructive). Set explicitly only when overriding.
   *
   * NOTE — this is the manager's `intent` prop, separate from the
   * registry's `config.intent`. Manager wins on conflict because the
   * manager knows which logical action is being confirmed; the
   * registry is a hint.
   */
  intent?: 'leave' | 'restart' | 'discard' | 'retake';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Manager-intent → visual taxonomy.
 *
 * `retake` was added 2026-04-28 (radar UX Phase B-1) so the post-
 * completion "redo this assessment" path uses the same chassis as the
 * in-flow Restart / Discard modals — replacing the standalone
 * `HeroRetakeModal` (Radix `AlertDialog`) which had its own primitive,
 * its own button order, and no Keep/Lose / arming visualisations.
 *
 * Visual intent: `warning` (amber) — destructive of the active result
 * but the prior result is archived (not lost). Distinct from
 * Discard's `destructive` red, which signals "deleted forever."
 */
const MANAGER_INTENT_TO_LIFECYCLE: Record<
  NonNullable<ConfirmModalProps['intent']>,
  { icon: React.ComponentType<{ className?: string }>; intent: LifecycleIntent }
> = {
  leave: { icon: Save, intent: 'info' },
  restart: { icon: RotateCcw, intent: 'warning' },
  discard: { icon: Trash2, intent: 'destructive' },
  retake: { icon: RefreshCw, intent: 'warning' },
};

export function ConfirmModal({
  open,
  config,
  context = {},
  busy = false,
  destructive = true,
  intent: managerIntent = 'discard',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const armed = useArming(open, config.arming ?? destructive);

  const interpolation = {
    progress: Math.round(context.progressPercent ?? 0),
    currentStep: context.currentStep ?? 1,
    totalSteps: context.totalSteps ?? 1,
  };

  // Resolve the visual intent: registry override (config.intent) wins
  // when set, otherwise fall back to the manager-supplied logical intent.
  const visualMap = MANAGER_INTENT_TO_LIFECYCLE[managerIntent];
  const intent: LifecycleIntent = config.intent ?? visualMap.intent;
  const Icon = visualMap.icon;

  const acknowledgeRequired = config.acknowledgeRequired ?? false;
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (!open) setAcknowledged(false);
  }, [open]);

  const canConfirm = !busy && armed && (!acknowledgeRequired || acknowledged);

  const hasKeepLose =
    (config.keep && config.keep.length > 0) || (config.lose && config.lose.length > 0);
  const hasLegacyPreFlight =
    !hasKeepLose && config.preFlightItems && config.preFlightItems.length > 0;

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md p-6"
        showCloseButton={false}
        // Destructive modals block Escape/overlay-dismiss so the user
        // makes an explicit choice. Non-destructive (Leave) lets it
        // pass through to onCancel.
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          if (!destructive && !busy) onCancel();
        }}
        onPointerDownOutside={(e) => {
          e.preventDefault();
          if (!destructive && !busy) onCancel();
        }}
      >
        {/*
         * Close X — always visible.
         *
         * Affordance ≠ behaviour. Hiding the X on destructive modals
         * (the pre-2026-04-28 behaviour) trapped users into reading
         * the cancel button text to find an escape; that's a usability
         * regression even though Escape was the keyboard backstop.
         * The X is now always rendered and routes through onCancel —
         * same end state as the Stay button, just a faster glance.
         * Escape and overlay-click are STILL blocked on destructive
         * modals (see onEscapeKeyDown above) to keep the keyboard
         * accident-rate low; the X is a deliberate click. Radar
         * 2026-04-28 — UX Phase B-5.
         */}
        <button
          type="button"
          onClick={() => !busy && onCancel()}
          disabled={busy}
          className="absolute top-4 right-4 rounded-xs text-muted-foreground hover:text-foreground transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-hidden disabled:pointer-events-none"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" aria-hidden />
          <span className="sr-only">{t('common.close')}</span>
        </button>

        <ModalEntrance>
          <IntentBadge intent={intent} icon={Icon} />

          <DialogHeader>
            <DialogTitle className="text-center text-lg leading-tight">
              {t(config.titleKey, { ...interpolation })}
            </DialogTitle>
            <DialogDescription className="pt-1.5 text-center text-sm text-muted-foreground leading-relaxed">
              {t(config.bodyKey, { ...interpolation })}
            </DialogDescription>
          </DialogHeader>

          {/* Progress snapshot — only meaningful for in-flight work.
              We render it for warning/destructive intents when the
              caller passed a percent; Leave keeps it too because it's
              reassuring ("you're 60% through, your work is saved"). */}
          {context.progressPercent !== undefined && context.progressPercent > 0 && (
            <div className="mt-3 space-y-1.5">
              <ProgressBar percent={context.progressPercent} />
              {context.currentStep && context.totalSteps && (
                <p className="text-center text-2xs text-muted-foreground">
                  {t('copilot.lifecycle.confirm.stepLabel', interpolation)}
                </p>
              )}
            </div>
          )}

          {/* Keep / Lose diff — the canonical "show the stakes" UI. */}
          {hasKeepLose && (
            <div className="mt-4">
              <KeepLoseList keep={config.keep} lose={config.lose} interpolation={interpolation} />
            </div>
          )}

          {/* Legacy single-list rendering — only shown when the registry
              hasn't migrated to the keep/lose diff yet. */}
          {hasLegacyPreFlight && config.preFlightItems && (
            <div className="mt-3">
              <PreFlightList items={config.preFlightItems} interpolation={interpolation} />
            </div>
          )}

          {/* Reversibility chip — sits just above the action footer so
              the user reads it last, right before deciding. */}
          {config.reversible !== undefined && (
            <div className="mt-4 flex justify-center">
              <ReversibilityChip reversible={config.reversible} />
            </div>
          )}

          {acknowledgeRequired && (
            <label className="flex items-start gap-2 text-sm py-2 mt-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={busy}
                aria-required="true"
              />
              <span className="text-foreground/85">
                {t(
                  config.acknowledgeLabelKey ?? 'copilot.lifecycle.acknowledge.default',
                  interpolation
                )}
              </span>
            </label>
          )}

          <div className="mt-5 flex flex-col gap-1">
            <ArmedConfirmButton
              intent={intent}
              armed={armed}
              busy={busy}
              disabled={!canConfirm && !busy}
              onClick={onConfirm}
              trailingIcon={managerIntent === 'leave' ? ArrowRight : undefined}
            >
              {t(config.confirmLabelKey, { ...interpolation })}
            </ArmedConfirmButton>
            <StayButton onClick={onCancel} busy={busy} labelKey={config.cancelLabelKey} />
          </div>
        </ModalEntrance>
      </DialogContent>
    </Dialog>
  );
}
